import { createTheme } from '@mui/material/styles';

export const getAppTheme = (mode = 'light') => {
  const isDark = mode === 'dark';
  return createTheme({
    palette: {
      mode,
      primary: { main: '#0A84FF', light: '#409CFF', dark: '#0060DF', contrastText: '#ffffff' },
      secondary: { main: isDark ? '#D1D5DB' : '#1A1F36' },
      background: {
        default: isDark ? '#0F1424' : '#F8FAFF',
        paper: isDark ? '#111827' : '#FFFFFF',
      },
      text: {
        primary: isDark ? '#E5E7EB' : '#1A1F36',
        secondary: isDark ? '#9CA3AF' : '#6B7280',
      },
      success: { main: '#32D74B' },
      warning: { main: '#FFD60A' },
      error: { main: '#FF453A' },
      info: { main: '#64D2FF' },
      divider: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)',
      // Additional accent palette to reduce blue monotony across UI elements
      accents: {
        indigo: '#6C5CE7',
        purple: '#8B5CF6',
        violet: '#7C3AED',
        pink: '#EC4899',
        rose: '#F43F5E',
        teal: '#14B8A6',
        cyan: '#06B6D4',
        sky: '#38BDF8',
        amber: '#F59E0B',
        lime: '#84CC16',
        emerald: '#10B981',
        slate: '#64748B',
      },
      // Premium brand tones for highlights and hero elements
      brand: {
        royalBlue: '#246BFD',
        electricViolet: '#7A5AF5',
        iris: '#5B8DEF',
        ocean: '#00A3FF',
        jade: '#00D084',
        gold: '#E6B905',
        coral: '#FF7A59',
        ruby: '#E11D48',
      },
      // Suggested tag/category colors (for chips/badges)
      tags: {
        site: '#06B6D4',
        device: '#8B5CF6',
        sensor: '#10B981',
        alarm: '#F43F5E',
        maintenance: '#F59E0B',
        forum: '#6C5CE7',
        analytics: '#38BDF8',
      },
      // Chart palette suggestions
      chart: {
        series1: '#0A84FF',
        series2: '#10B981',
        series3: '#F59E0B',
        series4: '#8B5CF6',
        series5: '#06B6D4',
        series6: '#F43F5E',
      },
      status: {
        normalGreen: '#2E7D32',
        alarmRed: '#D32F2F',
        warningAmber: '#FFA000',
        stoppedGrey: '#9E9E9E',
        manualBlue: '#1976D2',
      },
      grey: { 50: '#F9FAFB', 100: '#F5F7FB', 200: '#EDEFF3', 300: '#E5E7EB', 400: '#D1D5DB', 500: '#9CA3AF', 600: '#6B7280', 700: '#4B5563', 800: '#374151', 900: '#1F2937' },
    },
    // Subtle premium shadows (first 6 slots most used by MUI)
    shadows: [
      'none',
      isDark ? '0 1px 2px rgba(0,0,0,0.40)' : '0 1px 2px rgba(16,24,40,0.06)',
      isDark ? '0 1px 3px rgba(0,0,0,0.45), 0 1px 2px rgba(0,0,0,0.32)' : '0 1px 3px rgba(16,24,40,0.10), 0 1px 2px rgba(16,24,40,0.06)',
      isDark ? '0 2px 6px rgba(0,0,0,0.45)' : '0 2px 6px rgba(16,24,40,0.12)',
      isDark ? '0 4px 10px rgba(0,0,0,0.48)' : '0 4px 10px rgba(16,24,40,0.14)',
      isDark ? '0 6px 16px rgba(0,0,0,0.50)' : '0 6px 16px rgba(16,24,40,0.16)',
      ...Array(19).fill('none')
    ],
    typography: {
      fontFamily: 'Inter, system-ui, Segoe UI, Roboto, Arial, sans-serif',
      h1: { fontWeight: 700, fontSize: '2.5rem', letterSpacing: '-0.5px' },
      h2: { fontWeight: 700, fontSize: '2rem', letterSpacing: '-0.5px' },
      h3: { fontWeight: 600, fontSize: '1.75rem', letterSpacing: '-0.5px' },
      h4: { fontWeight: 600, fontSize: '1.5rem', letterSpacing: '-0.5px' },
      h5: { fontWeight: 600, fontSize: '1.25rem', letterSpacing: '-0.5px' },
      h6: { fontWeight: 600, fontSize: '1rem', letterSpacing: '-0.5px' },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.4)' : '0 4px 12px rgba(0,0,0,0.06)',
            backdropFilter: 'blur(8px)',
            backgroundColor: isDark ? 'rgba(17,24,39,0.92) !important' : 'rgba(255,255,255,0.92) !important',
            borderBottom: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.12)'
          }
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backdropFilter: 'blur(8px)',
            backgroundColor: isDark ? 'rgba(17,24,39,0.96)' : 'rgba(255,255,255,0.96)',
            borderRight: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.12)'
          }
        }
      },
      MuiButton: { 
        styleOverrides: { 
          root: { borderRadius: 10, padding: '8px 16px', fontWeight: 600 },
          contained: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
          outlined: { borderWidth: '1px', '&:hover': { borderWidth: '1px' } },
          text: { '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' } },
        },
        variants: [
          // Soft buttons using premium accents
          {
            props: { variant: 'soft', color: 'primary' },
            style: {
              backgroundColor: isDark ? 'rgba(10,132,255,0.18)' : 'rgba(10,132,255,0.10)',
              color: '#0A84FF',
              border: `1px solid ${isDark ? 'rgba(10,132,255,0.30)' : 'rgba(10,132,255,0.22)'}`,
              '&:hover': { backgroundColor: isDark ? 'rgba(10,132,255,0.24)' : 'rgba(10,132,255,0.14)' },
            }
          },
          {
            props: { variant: 'soft', color: 'success' },
            style: {
              backgroundColor: isDark ? 'rgba(16,185,129,0.18)' : 'rgba(16,185,129,0.10)',
              color: '#10B981',
              border: `1px solid ${isDark ? 'rgba(16,185,129,0.30)' : 'rgba(16,185,129,0.22)'}`,
              '&:hover': { backgroundColor: isDark ? 'rgba(16,185,129,0.24)' : 'rgba(16,185,129,0.14)' },
            }
          },
          {
            props: { variant: 'soft', color: 'warning' },
            style: {
              backgroundColor: isDark ? 'rgba(245,158,11,0.18)' : 'rgba(245,158,11,0.10)',
              color: '#F59E0B',
              border: `1px solid ${isDark ? 'rgba(245,158,11,0.30)' : 'rgba(245,158,11,0.22)'}`,
              '&:hover': { backgroundColor: isDark ? 'rgba(245,158,11,0.24)' : 'rgba(245,158,11,0.14)' },
            }
          },
          {
            props: { variant: 'soft', color: 'info' },
            style: {
              backgroundColor: isDark ? 'rgba(6,182,212,0.18)' : 'rgba(6,182,212,0.10)',
              color: '#06B6D4',
              border: `1px solid ${isDark ? 'rgba(6,182,212,0.30)' : 'rgba(6,182,212,0.22)'}`,
              '&:hover': { backgroundColor: isDark ? 'rgba(6,182,212,0.24)' : 'rgba(6,182,212,0.14)' },
            }
          },
          {
            props: { variant: 'soft', color: 'error' },
            style: {
              backgroundColor: isDark ? 'rgba(244,63,94,0.18)' : 'rgba(244,63,94,0.10)',
              color: '#F43F5E',
              border: `1px solid ${isDark ? 'rgba(244,63,94,0.30)' : 'rgba(244,63,94,0.22)'}`,
              '&:hover': { backgroundColor: isDark ? 'rgba(244,63,94,0.24)' : 'rgba(244,63,94,0.14)' },
            }
          },
        ]
      },
      MuiCard: { styleOverrides: { root: { borderRadius: 16, boxShadow: isDark ? '0 8px 28px rgba(0,0,0,0.45)' : '0 6px 18px rgba(16,24,40,0.10)', backgroundColor: isDark ? '#111827' : '#FFFFFF', border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)' } } },
      MuiPaper: { styleOverrides: { root: { borderRadius: 16, backgroundColor: isDark ? '#111827' : undefined } } },
      MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 10 }, inputSizeSmall: { height: 36, paddingTop: 8, paddingBottom: 8 } } },
      MuiTableCell: { styleOverrides: { root: { borderBottom: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.05)', padding: '12px 16px', color: isDark ? '#E5E7EB' : undefined }, head: { backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)', fontWeight: 600 } } },
      MuiChip: { 
        styleOverrides: { 
          root: { borderRadius: 8, fontWeight: 500, height: '28px' },
          colorPrimary: { backgroundColor: isDark ? 'rgba(10,132,255,0.12)' : '#F5F7FB', color: '#0A84FF', border: isDark ? '1px solid rgba(10,132,255,0.24)' : '1px solid rgba(10,132,255,0.18)' },
          colorSuccess: { backgroundColor: isDark ? 'rgba(16,185,129,0.15)' : 'rgba(16,185,129,0.10)', color: '#10B981', border: isDark ? '1px solid rgba(16,185,129,0.30)' : '1px solid rgba(16,185,129,0.25)' },
          colorWarning: { backgroundColor: isDark ? 'rgba(245,158,11,0.15)' : 'rgba(245,158,11,0.10)', color: '#F59E0B', border: isDark ? '1px solid rgba(245,158,11,0.30)' : '1px solid rgba(245,158,11,0.25)' },
          colorInfo: { backgroundColor: isDark ? 'rgba(6,182,212,0.15)' : 'rgba(6,182,212,0.10)', color: '#06B6D4', border: isDark ? '1px solid rgba(6,182,212,0.30)' : '1px solid rgba(6,182,212,0.25)' },
          colorError: { backgroundColor: isDark ? 'rgba(244,63,94,0.15)' : 'rgba(244,63,94,0.10)', color: '#F43F5E', border: isDark ? '1px solid rgba(244,63,94,0.30)' : '1px solid rgba(244,63,94,0.25)' },
        } 
      },
      MuiIconButton: { styleOverrides: { root: { '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', color: '#0A84FF' } } } },
      MuiTooltip: { styleOverrides: { tooltip: { fontSize: 12 } } },
      MuiMenu: { styleOverrides: { paper: { border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.06)', boxShadow: isDark ? '0 12px 28px rgba(0,0,0,0.5)' : '0 8px 24px rgba(0,0,0,0.08)' } } },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
            borderRadius: 12,
            backgroundColor: isDark ? '#0F1424' : '#FFFFFF',
            boxShadow: isDark ? '0 6px 18px rgba(0,0,0,0.35)' : '0 4px 12px rgba(0,0,0,0.04)',
            '--DataGrid-rowHeight': '44px',
            '--DataGrid-densityFactor': 1,
            color: isDark ? '#E5E7EB' : undefined,
          },
          columnHeaders: {
            backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
            borderBottom: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
          },
          cell: { borderBottom: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.05)' },
          row: { '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(10,132,255,0.04)' } },
          toolbarContainer: { padding: '8px', borderBottom: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)' },
        },
      },
      MuiSnackbar: {
        defaultProps: { anchorOrigin: { vertical: 'top', horizontal: 'right' }, autoHideDuration: 4000 },
        styleOverrides: { root: { '& .MuiPaper-root': { borderRadius: 12, boxShadow: isDark ? '0 12px 28px rgba(0,0,0,0.6)' : '0 8px 24px rgba(0,0,0,0.12)' } } }
      },
      MuiAlert: {
        defaultProps: { variant: 'filled', iconMapping: {} },
        styleOverrides: {
          root: { borderRadius: 10, boxShadow: 'none' },
          filledSuccess: { backgroundColor: '#32D74B', color: '#0B140B' },
          filledError: { backgroundColor: '#FF453A', color: '#1F0B0B' },
          filledWarning: { backgroundColor: '#FFD60A', color: '#332B00' },
          filledInfo: { backgroundColor: '#0A84FF', color: isDark ? '#EAF4FF' : '#001A2E' },
        }
      },
      MuiSkeleton: { styleOverrides: { root: { borderRadius: 10, backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', '&::after': { background: isDark ? 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)' : 'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)' } } } },
    },
  });
};

export default getAppTheme('light');